import { MenuFooter } from "./MenuFooter";
import { useMenuData } from "../menu-provider/MenuDataProvider";
import styled from "styled-components";
import { useTableNameFromURL } from "./tableNameFromURL";
import { useCart } from "./cart-provider/CartProvider";
import React from "react";
import { Category } from "./Category";
import { ScrollSpyTabs } from "../scrollspy/ScrollSpyTabs";
import { Helmet } from "react-helmet-async";
import { useMenuIdFromURL } from "../hooks/menuIdFromURL";
import { Avatar, Box, Divider, Skeleton, Typography } from "@mui/material";

const CategoryWrapper = styled.div`
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const BgImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  inset: 0;
`;

export const CustomerFacingMenuSkeleton = () => {
  return (
    <CategoryWrapper>
      <Box margin={1} data-testid="test-id-menu-loading-skeleton">
        <Skeleton variant="rectangular" width={"100%"} height={"60px"} />

        <Skeleton
          variant="rectangular"
          width={"100%"}
          height={"120px"}
          sx={{ marginTop: "16px" }}
        />

        <Divider sx={{ marginTop: "16px", marginBottom: "16px" }} />

        <Skeleton variant="rectangular" width={"100%"} height={"120px"} />
        <Divider sx={{ marginTop: "16px", marginBottom: "16px" }} />

        <Skeleton variant="rectangular" width={"100%"} height={"120px"} />
        <Divider sx={{ marginTop: "16px", marginBottom: "16px" }} />

        <Skeleton variant="rectangular" width={"100%"} height={"120px"} />
        <Divider sx={{ marginTop: "16px", marginBottom: "16px" }} />

        <Skeleton variant="rectangular" width={"100%"} height={"120px"} />
        <Divider sx={{ marginTop: "16px", marginBottom: "16px" }} />
      </Box>
    </CategoryWrapper>
  );
};

export const CustomerFacingMenu = () => {
  const menu = useMenuData();
  const { increaseItemQuantity, itemIdAndQuantity } = useCart();
  const tableName = useTableNameFromURL();
  const menuId = useMenuIdFromURL();

  return (
    <div style={{ paddingBottom: "120px" }}>
      <Helmet>
        <title>{menu.restaurantName}</title>
        <meta property="og:title" content={menu.restaurantName} />

        <meta
          property="og:url"
          content={`https://m.orderit.today/menu/${menuId}?t=${tableName}`}
        />

        <meta property="og:description" content="View our menu" />
      </Helmet>
      <MenuCover
        logo={menu.logo}
        themeColor={menu.themeColor}
        restaurantName={menu.restaurantName}
        coverImage={menu.coverImage}
        phone={menu.phone}
      />

      {menu.restaurantName === "" ? (
        <CustomerFacingMenuSkeleton />
      ) : (
        <CategoryWrapper>
          <ScrollSpyTabs
            tabsInScroll={menu.categories
              .filter(
                (category) =>
                  category.items.filter((item) => item.isEnabled).length !== 0
              )
              .map((category, index) => ({
                text: category.name,
                component: (
                  <React.Fragment key={`${category.name}${index}`}>
                    <Category
                      name={category.name}
                      description={category.description}
                      items={category.items}
                      key={index}
                      isCartButtonRendered={
                        menu.isOrderingEnabled && !!tableName
                      }
                      currency={menu.currency}
                      onIncreaseItemQuantity={increaseItemQuantity}
                      itemIdAndQuantity={itemIdAndQuantity}
                    />
                  </React.Fragment>
                ),
              }))}
          />
        </CategoryWrapper>
      )}
      <MenuFooter />
    </div>
  );
};

function MenuCover({
  themeColor,
  logo,
  restaurantName,
  coverImage,
  phone,
}: {
  themeColor: string;
  logo?: string;
  restaurantName: string;
  coverImage?: string;
  phone: string;
}) {
  return (
    <Box
      height="160px"
      bgcolor={themeColor}
      position="relative"
      overflow="hidden"
    >
      {!!coverImage && <BgImg src={coverImage} alt="" />}

      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        sx={{
          background: "linear-gradient(transparent, rgba(0,0,0,0.65))",
          pt: 5,
          pb: 1.5,
          px: 2,
          display: "flex",
          alignItems: "flex-end",
          gap: 1.5,
        }}
      >
        <Avatar
          src={logo}
          sx={{
            width: 52,
            height: 52,
            fontSize: "22px",
            flexShrink: 0,
            border: "2px solid rgba(255,255,255,0.85)",
          }}
        >
          {restaurantName[0]}
        </Avatar>

        <Box minWidth={0}>
          <Typography
            variant="subtitle1"
            color="white"
            fontWeight={700}
            noWrap
            sx={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)", lineHeight: 1.2 }}
          >
            {restaurantName}
          </Typography>
          {phone && (
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.82)",
                textShadow: "0 1px 2px rgba(0,0,0,0.4)",
              }}
            >
              {phone}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
